import { createSelector } from 'reselect';
import { i18n, InjectorHelper } from 'invision-core';
import { findIndex, flatten, pathOr, uniq } from 'ramda';
import LocaleKeys from './../../locales/keys';
import * as SubscriptionConstants from '../../components/shared/constants/subscriptions.constants';
import { CurrentSubscriptionItemExtensionIdSelector, CurrentSubscriptionSelector, CurrencySelector, RemoveChildSubscriptionSelector, SubscriptionPricingPlanIdSelector, SubscriptionProductIdSelector, UserCanModifySelector, UserCanRemoveSubscriptionSelector, UserCanReplaceSubscriptionSelector, UserCanRenewSelector } from './customer.subscriptions.selectors';
import { ProductsWithMetadataMapSelector, ShoppingCartSelector } from './../../reducers/selectors/products.order.selectors';
function getItemWithViewProperties(item, currency, productsMap) {
  var $filter = InjectorHelper.getFilterService();
  var translatedYes = i18n.translate(LocaleKeys.YES);
  var translatedNo = i18n.translate(LocaleKeys.NO);
  var grossRenewAmountEval = item.GrossRenewAmount;
  var renewEval = item.Renew;
  var renewAmountEval = item.RenewAmount;
  var shippedEval = item.Shipped;
  var instanceProps = [];
  if (item.InstanceProperties && item.Product && productsMap[item.Product.Id]) {
    var _loop = function _loop(key) {
      var metaIp = productsMap[item.Product.Id].InstanceProperties.find(function (mip) {
        return mip.Id.toString() === key;
      });
      if (metaIp) {
        var ip = {
          Name: metaIp.Name,
          Value: item.InstanceProperties[key]
        };
        instanceProps.push(ip);
      }
    };
    for (var key in item.InstanceProperties) {
      _loop(key);
    }
  }
  return item.set('GrossRenewAmount', grossRenewAmountEval && currency && currency.length > 0 ? $filter('invCurrency')(grossRenewAmountEval, currency) : i18n.translate(LocaleKeys.NOT_APPLICABLE)).set('uiInstanceProperties', instanceProps).set('Renew', renewEval ? translatedYes : translatedNo).set('RenewAmount', renewAmountEval && currency && currency.length > 0 ? $filter('invCurrency')(renewAmountEval, currency) : i18n.translate(LocaleKeys.NOT_APPLICABLE)).set('Shipped', shippedEval ? translatedYes : translatedNo);
}
var DEFAULT_SUBSCRIPTION_ITEMS = [];
export var SubscriptionWithDetailedItemsSelector = createSelector([CurrentSubscriptionSelector, RemoveChildSubscriptionSelector, CurrencySelector, ProductsWithMetadataMapSelector], function (subscription, childSubscription, currency, productsMap) {
  if (subscription) {
    if (childSubscription) {
      var subscriptionItem = getItemWithViewProperties(childSubscription, currency, productsMap);
      if (childSubscription.Children) {
        subscriptionItem = subscriptionItem.set('Children', subscriptionItem.Children.map(function (childItem) {
          return getItemWithViewProperties(childItem, currency, productsMap);
        }));
      }
      return [subscriptionItem];
    }
    return subscription.Items.map(function (item) {
      var subscriptionItem = getItemWithViewProperties(item, currency, productsMap);
      if (subscriptionItem.Children && subscriptionItem.Children.length) {
        subscriptionItem = subscriptionItem.set('Children', subscriptionItem.Children.map(function (childItem) {
          return getItemWithViewProperties(childItem, currency, productsMap);
        }));
      }
      return subscriptionItem;
    }, []);
  }
  return DEFAULT_SUBSCRIPTION_ITEMS;
});
export var SubscriptionItemsExtensionSelectors = createSelector([SubscriptionWithDetailedItemsSelector], function (subscriptionItems) {
  if (subscriptionItems.length) {
    var itemExtensions = subscriptionItems.map(function (item) {
      if (item.SubscriptionExtensions) {
        return item.SubscriptionExtensions;
      } else {
        return [];
      }
    });
    //flatten takes arrays inside arrays and places them inside the original array
    //i.e. [1,2,[3,4],5] => [1,2,3,4,5]
    return uniq(flatten(itemExtensions));
  } else {
    return null;
  }
});
export var CurrentSubscriptionItemWithExtensionSelectors = createSelector([CurrentSubscriptionItemExtensionIdSelector, SubscriptionWithDetailedItemsSelector], function (currentExtensionId, subscriptionItems) {
  var items = subscriptionItems.filter(function (item) {
    return item.SubscriptionExtensions ? item.SubscriptionExtensions.some(function (extensionItem) {
      return extensionItem.Id === parseInt(currentExtensionId);
    }) : null;
  });
  return items.length ? items[0] : null;
});
export var CurrentSubscriptionItemExtensionSelectors = createSelector([CurrentSubscriptionItemExtensionIdSelector, SubscriptionItemsExtensionSelectors], function (currentId, extensionItems) {
  var extension = null;
  if (currentId && extensionItems) {
    extension = extensionItems.find(function (item) {
      return item.Id === parseInt(currentId);
    });
  }
  return extension || null;
});
export var CurrentSubscriptionItemExtensionPricingPlanSelectors = createSelector([CurrentSubscriptionItemExtensionIdSelector, CurrentSubscriptionItemWithExtensionSelectors, CurrentSubscriptionItemExtensionSelectors], function (currentExtensionId, subscriptionItem, subscriptionItemExtension) {
  if (!currentExtensionId || !subscriptionItem || !subscriptionItemExtension) {
    return null;
  } else {
    if (subscriptionItemExtension.PricingPlanId === subscriptionItem.PricingPlan.Id) {
      return subscriptionItem.PricingPlan;
    }
    var option = subscriptionItem.ChangeOptions.find(function (option) {
      return option.PricingPlan.Id === subscriptionItemExtension.PricingPlanId;
    });
    return pathOr(null, ['PricingPlan'], option);
  }
});
export var SubscriptionShoppingCartIndexSelector = createSelector([SubscriptionPricingPlanIdSelector, SubscriptionProductIdSelector, ShoppingCartSelector], function (pricingPlanId, productId, shoppingCart) {
  if (!pricingPlanId || !productId || !shoppingCart || !shoppingCart.Items) {
    return undefined;
  }
  return findIndex(function (item) {
    return item.PricingPlanId === pricingPlanId && item.ProductId === productId;
  }, shoppingCart.Items);
});
export var SubscriptionProductsMoreOptionsMapSelector = createSelector([CurrentSubscriptionSelector, UserCanRemoveSubscriptionSelector, UserCanReplaceSubscriptionSelector, UserCanModifySelector, UserCanRenewSelector], function (currentSubscription, userCanRemove, userCanReplace, userCanModify, userCanRenew) {
  if (!currentSubscription || !currentSubscription.isAligned || currentSubscription.offeringThumbnail) {
    return {};
  }
  var moreOptionsMap = {};
  currentSubscription.Items.forEach(function (subscriptionItem) {
    var moreOptions = [];
    if (userCanModify) {
      moreOptions.push({
        id: SubscriptionConstants.MODIFY_SUBSCRIPTION,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.MODIFY)
      });
    }
    if (userCanRemove) {
      moreOptions.push({
        id: SubscriptionConstants.REMOVE_SUBSCRIPTION_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.REMOVE_SUBSCRIPTION.REMOVE)
      });
    }
    if (userCanReplace) {
      moreOptions.push({
        id: SubscriptionConstants.REPLACE_SUBSCRIPTION_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.REPLACE_SUBSCRIPTION)
      });
    }
    if (userCanRenew) {
      // TODO - will be uncommented and implemented at subsr item level with ASC-67209
      moreOptions.push({
        id: SubscriptionConstants.MODIFY_RENEWAL_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.MODIFY_RENEWAL)
      });
    }
    moreOptionsMap[subscriptionItem.Id] = moreOptions;
  });
  return moreOptionsMap;
});